import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const Landing = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('home');
  const [copied, setCopied] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const SUPPORT_PHONE = '0725965086';
  const USDT_WALLET_ADDRESS = 'TEHyFX9roJFkVCg5t6NrPUJHFonR8p8pCu';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(USDT_WALLET_ADDRESS);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const scrollToSection = (id) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const features = [
    { icon: '⚡', title: 'Lightning Execution', desc: 'Millisecond trades via MetaApi' },
    { icon: '📊', title: 'Smart Analysis', desc: 'SMA • EMA • RSI • StdDev' },
    { icon: '🤖', title: '5 Trading Bots', desc: 'Gold • EURUSD • GBPUSD • USDJPY • BTC' },
    { icon: '🛡️', title: 'Bank-Grade Security', desc: 'Encrypted • Magic # isolation' },
    { icon: '📈', title: 'Multi-Asset', desc: 'Forex • Gold • Crypto' },
    { icon: '⏰', title: '24/7 Automation', desc: 'Always active' },
  ];

  const faqs = [
    { q: 'What is TidalWave Pro?', a: 'Automated trading system with 5 specialized bots using technical indicators and MetaApi.' },
    { q: 'Do I need experience?', a: 'No — fully automated with optimized defaults for beginners.' },
    { q: 'Is my account safe?', a: 'Credentials never stored. Encrypted connection + magic number protection.' },
    { q: 'Payment methods?', a: 'USDT (TRC20) — fast & low fees.' },
    { q: 'Can I cancel?', a: 'Yes, anytime. 7-day money-back guarantee.' },
    { q: 'How to start?', a: 'Pay → Send proof → We activate & assist setup.' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0f1e] via-[#0f1a3a] to-[#0a0f1e] text-white font-sans antialiased">
      {/* Floating Support */}
      <a
        href={`tel:${SUPPORT_PHONE}`}
        className="fixed bottom-5 right-5 z-50 flex items-center gap-3 bg-gradient-to-r from-cyan-700 to-blue-800 px-5 py-3.5 rounded-full shadow-2xl shadow-cyan-900/50 hover:shadow-cyan-600/70 hover:scale-105 transition-all duration-300"
      >
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-xl">
          📞
        </div>
        <div className="flex flex-col text-sm">
          <span className="opacity-90 text-xs">Support</span>
          <span className="font-semibold tracking-wide">{SUPPORT_PHONE}</span>
        </div>
      </a>

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
          scrolled ? 'bg-[#0f1429]/90 backdrop-blur-xl border-b border-cyan-900/40 shadow-2xl' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 bg-gradient-to-br from-cyan-600 to-blue-700 rounded-lg flex items-center justify-center shadow-cyan-600/40">
                <span className="text-xl">🌊</span>
              </div>
              <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-cyan-200 to-blue-300 bg-clip-text text-transparent">
                TidalWave
              </span>
            </div>

            <div className="hidden md:flex items-center gap-7">
              {['home', 'features', 'pricing', 'faq'].map((id) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    activeSection === id ? 'text-cyan-300' : 'text-cyan-100/80 hover:text-cyan-200'
                  }`}
                >
                  {id.charAt(0).toUpperCase() + id.slice(1)}
                </button>
              ))}
              <button
                onClick={() => navigate('/license')}
                className="px-5 py-2 bg-gradient-to-r from-cyan-600 to-blue-700 rounded-lg text-sm font-semibold hover:scale-105 transition-all shadow-cyan-700/30"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section id="home" className="pt-24 sm:pt-32 pb-16 px-5 sm:px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-5">
            Smart Automated Trading
            <br />
            <span className="bg-gradient-to-r from-cyan-200 to-blue-300 bg-clip-text text-transparent">
              TidalWave Pro
            </span>
          </h1>

          <p className="text-base sm:text-lg text-cyan-100/80 max-w-2xl mx-auto mb-10">
            5 specialized bots • Real-time indicators • MetaApi execution • 24/7
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => navigate('/license')}
              className="px-8 py-4 bg-gradient-to-r from-cyan-600 to-blue-700 rounded-xl font-semibold text-base hover:scale-[1.03] transition-all shadow-cyan-700/40 shadow-lg"
            >
              Start Now
            </button>
            <button
              onClick={() => scrollToSection('features')}
              className="px-8 py-4 border border-cyan-800/50 hover:border-cyan-500 rounded-xl text-base transition-all hover:bg-cyan-900/20"
            >
              Learn More
            </button>
          </div>

          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center text-sm">
            <div><span className="text-2xl font-bold text-cyan-300">$2.5B+</span><p className="text-cyan-100/70 mt-1">Volume</p></div>
            <div><span className="text-2xl font-bold text-cyan-300">99.9%</span><p className="text-cyan-100/70 mt-1">Uptime</p></div>
            <div><span className="text-2xl font-bold text-cyan-300">150+</span><p className="text-cyan-100/70 mt-1">Countries</p></div>
            <div><span className="text-2xl font-bold text-cyan-300">24/7</span><p className="text-cyan-100/70 mt-1">Running</p></div>
          </div>
        </div>
      </section>

      {/* Features + How It Works */}
      <section id="features" className="py-16 sm:py-20 px-5 sm:px-6 bg-gradient-to-b from-[#0f1429]/60 to-transparent">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12 text-cyan-100">Features</h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {features.map((f, i) => (
              <div
                key={i}
                className="p-6 rounded-xl bg-[#0f1429]/50 border border-cyan-900/40 hover:border-cyan-600/60 transition-all hover:-translate-y-1 hover:shadow-cyan-900/30"
              >
                <div className="text-3xl mb-4">{f.icon}</div>
                <h3 className="text-lg font-semibold mb-2 text-cyan-100">{f.title}</h3>
                <p className="text-cyan-100/70 text-sm">{f.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-20">
            <h3 className="text-2xl sm:text-3xl font-bold text-center mb-10 text-cyan-100">How It Works</h3>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                { num: 1, title: 'Connect MT5', desc: 'Secure link via MetaApi' },
                { num: 2, title: 'Choose Bots', desc: 'Pick your markets' },
                { num: 3, title: 'Start Trading', desc: 'Automated 24/7' },
              ].map((step) => (
                <div key={step.num} className="text-center group">
                  <div className="relative mx-auto mb-5 w-20 h-20 rounded-full bg-gradient-to-br from-cyan-900/30 to-blue-900/30 border border-cyan-700/50 flex items-center justify-center text-3xl font-bold text-cyan-300 group-hover:scale-110 transition-transform duration-300">
                    {step.num}
                  </div>
                  <h4 className="text-lg font-semibold mb-2 text-cyan-100">{step.title}</h4>
                  <p className="text-cyan-100/70 text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16 sm:py-20 px-5 sm:px-6">
        <div className="max-w-md mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-8 text-cyan-100">Pricing</h2>

          <div className="p-8 rounded-2xl bg-[#0f1429]/60 border border-cyan-900/40 backdrop-blur-sm shadow-cyan-950/20">
            <div className="text-center">
              <div className="text-5xl font-extrabold text-cyan-100">$199</div>
              <div className="text-cyan-100/70 text-sm mt-1">/ month</div>
            </div>

            <ul className="my-8 space-y-3 text-sm text-cyan-100/80">
              {[
                'All 5 Bots',
                'MetaApi Connection',
                'Technical Indicators',
                'Risk Management',
                '24/7 Trading',
                'Priority Support',
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="text-cyan-400 text-lg">✓</span> {item}
                </li>
              ))}
            </ul>

            <button
              onClick={() => navigate('/license')}
              className="w-full py-4 bg-gradient-to-r from-cyan-600 to-blue-700 rounded-xl font-semibold hover:scale-[1.02] transition-all shadow-cyan-700/40"
            >
              Get Started
            </button>
          </div>
        </div>
      </section>

      {/* Support + Payment */}
      <section className="py-16 px-5 sm:px-6 bg-gradient-to-r from-cyan-950/20 to-blue-950/10">
        <div className="max-w-2xl mx-auto text-center">
          <div className="mb-12 p-7 rounded-2xl bg-[#0f1429]/60 border border-cyan-900/40">
            <h2 className="text-2xl sm:text-3xl font-bold mb-5 flex justify-center items-center gap-3 text-cyan-100">
              <span className="text-4xl">📞</span> Support
            </h2>
            <p className="text-cyan-100/80 mb-6">
              Questions before or after payment?<br />We're here to help.
            </p>
            <a
              href={`tel:${SUPPORT_PHONE}`}
              className="inline-flex gap-3 items-center px-8 py-4 bg-gradient-to-r from-cyan-700 to-blue-800 rounded-xl text-lg font-semibold hover:scale-105 transition-all shadow-cyan-800/40"
            >
              Call {SUPPORT_PHONE}
            </a>
          </div>

          <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-cyan-100">USDT (TRC20)</h2>
          <div className="bg-[#0f1429]/70 rounded-xl p-6 border border-cyan-900/40">
            <div className="font-mono text-sm break-all bg-[#0a0f1e] p-4 rounded-lg mb-5 border border-cyan-900/50 text-cyan-100">
              {USDT_WALLET_ADDRESS}
            </div>
            <button
              onClick={copyToClipboard}
              className={`w-full py-4 rounded-xl font-semibold transition-all ${
                copied ? 'bg-emerald-700 text-white' : 'bg-gradient-to-r from-cyan-600 to-blue-700 hover:brightness-110 shadow-cyan-700/40'
              }`}
            >
              {copied ? 'Copied ✓' : 'Copy Address'}
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 sm:py-20 px-5 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-10 text-cyan-100">FAQ</h2>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="rounded-xl overflow-hidden border border-cyan-900/40 bg-[#0f1429]/50">
                <button
                  onClick={() => toggleFaq(i)}
                  className="w-full px-5 py-4 text-left flex justify-between items-center hover:bg-cyan-900/20 transition-colors text-cyan-100 font-medium"
                >
                  <span>{faq.q}</span>
                  <span className={`text-cyan-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}>
                    ▼
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-cyan-100/80 text-sm">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="py-8 text-center text-cyan-100/60 text-sm border-t border-cyan-900/40">
        © {new Date().getFullYear()} TidalWave Pro
      </footer>
    </div>
  );
};

export default Landing;